import axios from 'axios';
import { getApiBaseUrl } from '../utils/env';
import { FailedVideoItem, VideoManagerItem } from '../components/types/videos';
import { VideoStatus } from '../components/notification/types';

const fetchVideoManagerSuccess = payload => ({
  type: 'FETCH_VIDEO_MANAGER_SUCCESS',
  payload,
});

const fetchVideoFailedSuccess = payload => ({
  type: 'FETCH_VIDEO_FAILED_SUCCESS',
  payload,
});

const updateBusyIndicator = payload => ({
  type: 'FETCH_VIDEO_LIBRARY_BUSY',
  payload,
});

const removeVideoUpload = (payload: VideoStatus) => ({
  type: 'REMOVE_VIDEO_UPLOAD',
  payload,
});

const updateVideoUpload = (payload: VideoStatus) => ({
  type: 'UPDATE_VIDEO_UPLOAD',
  payload,
});

export const updateImportVideoMessages = (payload: VideoStatus[]) => ({
  type: 'UPDATE_VIDEO_IMPORT_MESSAGES',
  payload,
});

export const apiVideoLibraryProvider =
  ({ publisher, toAction, ...params }) =>
    async dispatch => {
      dispatch(updateBusyIndicator(true));
      try {
        const { data } = await axios.get(`${getApiBaseUrl()}/${publisher}/video-library/manage${toAction ? '/to-action' : ''}`, {
          params,
        });
        dispatch(fetchVideoManagerSuccess(data));
        dispatch(updateBusyIndicator(false));
      } catch (e) {
        dispatch(updateBusyIndicator(false));
        console.error(e);
      }
    };

export const apiVideoToActionCountProvider =
  ({ publisher, ...params }) =>
    async dispatch => {
      const { data } = await axios.get(`${getApiBaseUrl()}/${publisher}/video-library/manage/to-action-count`, {
        params,
      });
      dispatch({
        type: 'UPDATE_VIDEO_TO_ACTION_COUNT',
        payload: data.total
      });
    };

export const apiUploadVideoProvider =
  ({ publisher: selectedOrganisation, file, video, title }) =>
    async dispatch => {
      if (!file) {
        return;
      }

      let isUploading = true;

      const preventUnload = (e) => {
        if (isUploading) {
          e.preventDefault();
          e.returnValue = 'An upload is in progress. Are you sure you want to leave?';
          return e.returnValue;
        }
      };

      window.addEventListener('beforeunload', preventUnload);

      const videoUpload: VideoStatus = {
        id: Math.floor(Math.random() * 10000000000),
        status: 'uploading',
        video_message: 'Uploading...',
        video_filename: file.name,
        video_url: '',
        progress: 0
      };

      const formData = new FormData();
      formData.append('file', file as Blob);

      const { data: preSignedUrl } = await axios.post(`${getApiBaseUrl()}/video-clip/${selectedOrganisation}/put-object-signed-url`, {
        filename: file?.name,
        uuid: video?.uuid,
        video_title: title,
        contentType: file.type,
      });

      await axios({
        method: 'put',
        url: preSignedUrl,
        data: file,
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          dispatch(updateVideoUpload({
            ...videoUpload,
            progress
          }));
        },
      }).then(() => {
        dispatch(updateVideoUpload({
          ...videoUpload,
          status: 'completed',
          video_message: 'Video uploaded successfully',
          progress: 100
        }));
        isUploading = false;
        setTimeout(() => dispatch(removeVideoUpload(videoUpload)), 5000);
        window.removeEventListener('beforeunload', preventUnload);
      })
        .catch((message) => {
          dispatch(updateVideoUpload({
            ...videoUpload,
            status: 'error',
            video_message: typeof message === 'string' ? message : 'Sorry, we are unable to process this request.',
            progress: 100
          }));
          isUploading = false;
          window.removeEventListener('beforeunload', preventUnload);
        });
    };

export const apiVideoFailedLibraryProvider =
  ({ publisher, ...params }) =>
    async dispatch => {
      dispatch(updateBusyIndicator(true));
      try {
        const { data } = await axios.get(`${getApiBaseUrl()}/video-log/${publisher}`, { params });
        dispatch(fetchVideoFailedSuccess(data));
        dispatch(updateBusyIndicator(false));
      } catch (e) {
        dispatch(updateBusyIndicator(false));
        console.error(e);
      }
    };

export const requestRHOrganisationLanguage =
  ({ publisher, format }) =>
    async dispatch => {
      try {
        const { data } = await axios.get(`${getApiBaseUrl()}/video-transcript/rightsholder/${format}/${publisher}`);
        dispatch({
          type: 'UPDATE_RH_LANGUAGE',
          payload: data
        });

      } catch (e) {}
    }

export interface VideoManagerState {
  videos: VideoManagerItem[];
  failedVideos: FailedVideoItem[];
  videoFilterFormat: string;
  searchText: string;
  uploadVideos: VideoStatus[];
  importVideos: VideoStatus[];
  rhLang: string[];
  busy: boolean;
  videosStale: boolean;
  videoToActionCount: number;
  pagination: {
    currentPage: number;
    itemCount: number;
    itemsPerPage: number;
    totalItems: number;
    totalPages: number;
  };
}

const initialState: VideoManagerState = {
  videos: [],
  failedVideos: [],
  videoFilterFormat: 'horizontal',
  searchText: '',
  busy: false,
  rhLang: [],
  uploadVideos: [],
  importVideos: [],
  videosStale: false,
  videoToActionCount: 0,
  pagination: {
    currentPage: 1,
    itemCount: 0,
    itemsPerPage: 20,
    totalItems: 0,
    totalPages: 0,
  },

};

const reducer = (state = initialState, action: { type: string, payload: Record<string, any> }) => {
  switch (action.type) {
    case 'UPDATE_VIDEO_PROPERTY': {
      const { uuid, ...rest } = action.payload;
      return {
        ...state,
        videos: state.videos.map(video => {
          if (video.uuid === uuid) {
            return {
              ...video,
              ...rest
            }
          }
          return video;
        })
      }
    }
    case 'REMOVE_VIDEO_FROM_LIST': {
      return {
        ...state,
        videos: state.videos.map(video => {
          if (video.uuid === action.payload.uuid) {
            return {
              ...video,
              removed: true
            }
          }
          return video;
        })
      }
    }
    case 'UPDATE_VIDEO_TO_ACTION_COUNT': {
      return {
        ...state,
        videoToActionCount: action.payload
      };
    }
    case 'UPDATE_RH_LANGUAGE': {
      return {
        ...state,
        rhLang: action.payload,
      };
    }
    case 'UPDATE_VIDEO_LIST_STALE': {
      return {
        ...state,
        videosStale: action.payload,
      };
    }
    case 'UPDATE_VIDEO_IMPORT_MESSAGES': {
      return {
        ...state,
        importVideos: action.payload,
      };
    }
    case 'RESET_PAGINATION': {
      return {
        ...state,
        pagination: {
          ...initialState.pagination,
        },
      };
    }
    case 'SET_PAGINATION':
      return {
        ...state,
        pagination: {
          ...state.pagination,
          ...action.payload,
        },
      };
    case 'FETCH_VIDEO_FAILED_SUCCESS': {
      const { items, meta } = action.payload;
      return {
        ...state,
        failedVideos: items,
        pagination: {
          ...state.pagination,
          ...meta
        }
      };
    }
    case 'SET_FILTER_VIDEO_LIBRARY':
      return {
        ...state,
        videoFilterFormat: action.payload,
        pagination: {
          ...state.pagination,
          currentPage: 1,
        },
      };
    case 'SET_VIDEO_LIBRARY_SEARCH_TEXT':
      return {
        ...state,
        searchText: action.payload,
      };
    case 'FETCH_VIDEO_MANAGER_SUCCESS':
      let { items: videos, meta: pagination } = action.payload;
      return {
        ...state,
        videosStale: false,
        videos,
        pagination,
      };

    case 'UPDATE_VIDEO_UPLOAD': {
      const needUpdate = state.uploadVideos.find(video => video.id === action.payload.id);
      if (needUpdate) {
        return {
          ...state,
          uploadVideos: state.uploadVideos.map(video => {
            if (video.id === action.payload.id) {
              return action.payload;
            }
            return video;
          })
        };
      }

      return {
        ...state,
        uploadVideos: [...state.uploadVideos, action.payload]
      };
    }

    case 'REMOVE_VIDEO_UPLOAD': {
      return {
        ...state,
        uploadVideos: state.uploadVideos.filter(video => video.id !== action.payload.id)
      };
    }

    case 'FETCH_VIDEO_LIBRARY_BUSY':
      return {
        ...state,
        busy: action.payload,
      };

    case 'RESET_BEFORE_SWITCH_TAB': {
      return {
        ...state,
        pagination: initialState.pagination,
        videos: initialState.videos,
      };
    }

    case 'RESET_VIDEO_MANAGER':
      return initialState;

    default:
      return state;
  }
};

export default reducer;
